import React, { useEffect, useMemo, useState } from "react";
import { FaBell, FaCalendarAlt, FaFileAlt, FaStethoscope, FaCheckDouble } from "react-icons/fa";
import Header from "../Components/Header";
import Footer from "../Components/Footer";
import EmptyState from "../Components/States/EmptyState";
import OfflineState from "../Components/States/OfflineState";
import LoadingState from "../Components/States/LoadingState";
import { isNetworkError } from "../utils/network";
import { getNotifications, markNotificationsRead } from "../api/notifications.api";
import { getStaffId } from "../utils/storage";
import { apiErrorMessage } from "../utils/apiError";
import { toast } from "../Components/Toast";

const timeAgo = (timestamp) => {
  const now = new Date();
  const past = new Date(timestamp);
  const diffMs = now - past;
  const diffMins = Math.floor(diffMs / 60000);
  const diffHours = Math.floor(diffMs / 3600000);
  const diffDays = Math.floor(diffMs / 86400000);
  if (diffMins < 1) return "Just now";
  if (diffMins < 60) return `${diffMins}m ago`;
  if (diffHours < 24) return `${diffHours}h ago`;
  if (diffDays < 7) return `${diffDays}d ago`;
  return past.toLocaleDateString("en-GB", { day: "numeric", month: "short" });
};

const groupLabel = (timestamp) => {
  const d = new Date(timestamp);
  const today = new Date();
  const yesterday = new Date(today);
  yesterday.setDate(yesterday.getDate() - 1);
  if (d.toDateString() === today.toDateString()) return "Today";
  if (d.toDateString() === yesterday.toDateString()) return "Yesterday";
  return d.toLocaleDateString("en-US", { weekday: "long", month: "short", day: "numeric" });
};

const getIcon = (title) => {
  const t = (title || "").toLowerCase();
  if (t.includes("booking") || t.includes("appointment")) {
    return (
      <div className="w-10 h-10 sm:w-11 sm:h-11 flex items-center justify-center rounded-xl bg-primary-fixed/30 shrink-0">
        <FaCalendarAlt className="text-primary text-base sm:text-lg" />
      </div>
    );
  }
  if (t.includes("doctor") || t.includes("consult")) {
    return (
      <div className="w-10 h-10 sm:w-11 sm:h-11 flex items-center justify-center rounded-xl bg-primary-fixed/30 shrink-0">
        <FaStethoscope className="text-primary text-base sm:text-lg" />
      </div>
    );
  }
  if (t.includes("package")) {
    return (
      <div className="w-10 h-10 sm:w-11 sm:h-11 flex items-center justify-center rounded-xl bg-orange-100 shrink-0">
        <FaFileAlt className="text-orange-500 text-base sm:text-lg" />
      </div>
    );
  }
  return (
    <div className="w-10 h-10 sm:w-11 sm:h-11 flex items-center justify-center rounded-xl bg-surface-low shrink-0">
      <FaBell className="text-on-surface-variant text-base sm:text-lg" />
    </div>
  );
};

const NotificationsPage = () => {
  const [notifications, setNotifications] = useState([]);
  const [serverUnread, setServerUnread] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [networkError, setNetworkError] = useState(false);
  const staffId = getStaffId();

  const loadNotifications = () => {
    if (!staffId) {
      setError("Please log in to view notifications.");
      setLoading(false);
      return;
    }
    setLoading(true);
    setNetworkError(false);
    setError(null);
    getNotifications(staffId)
      .then((response) => {
        if (response.data.notifications) {
          setNotifications(response.data.notifications);
        }
        if (typeof response.data.unreadCount === "number") {
          setServerUnread(response.data.unreadCount);
        }
      })
      .catch((err) => {
        if (isNetworkError(err)) setNetworkError(true);
        else setError("Failed to fetch notifications. Please try again later.");
      })
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    loadNotifications();
  }, [staffId]);

  const handleMarkRead = (notification) => {
    if (!notification || notification.isRead) return;
    const id = notification._id;
    if (!id) return;
    markNotificationsRead(id)
      .then((res) => {
        setNotifications((prev) => prev.map((n) => (n._id === id ? { ...n, isRead: true } : n)));
        if (typeof res.data?.unreadCount === "number") setServerUnread(res.data.unreadCount);
      })
      .catch((err) => toast.error(apiErrorMessage(err, "Could not mark notification as read.")));
  };

  const handleMarkAllRead = () => {
    markNotificationsRead()
      .then((res) => {
        setNotifications((prev) => prev.map((n) => ({ ...n, isRead: true })));
        setServerUnread(typeof res.data?.unreadCount === "number" ? res.data.unreadCount : 0);
      })
      .catch((err) => toast.error(apiErrorMessage(err, "Could not mark notifications as read.")));
  };

  const unreadCount = serverUnread != null
    ? serverUnread
    : notifications.filter((n) => !n.isRead).length;

  const grouped = useMemo(() => {
    const sorted = [...notifications].reverse();
    return sorted.reduce((acc, n) => {
      const label = groupLabel(n.timestamp);
      if (!acc[label]) acc[label] = [];
      acc[label].push(n);
      return acc;
    }, {});
  }, [notifications]);

  return (
    <div className="min-h-screen bg-surface-low flex flex-col">
      <Header />
      <div className="flex flex-col min-h-screen pb-16 lg:pb-0 flex-1">
        <div className="max-w-2xl mx-auto w-full px-3 sm:px-4 py-5 sm:py-8">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-5 sm:mb-6">
            <div>
              <h1 className="text-xl sm:text-2xl font-bold text-on-primary-fixed-var">Notifications</h1>
              <p className="text-sm text-on-surface-variant mt-0.5">
                {unreadCount > 0 ? `${unreadCount} unread` : "All caught up"}
              </p>
            </div>
            {unreadCount > 0 && (
              <button
                type="button"
                onClick={handleMarkAllRead}
                className="flex items-center justify-center gap-1.5 text-sm font-medium text-primary hover:underline w-full sm:w-auto py-2 sm:py-0 rounded-lg sm:rounded-none bg-primary-fixed/10 sm:bg-transparent"
              >
                <FaCheckDouble size={12} />
                Mark all read
              </button>
            )}
          </div>

          {networkError && <OfflineState onRetry={loadNotifications} />}

          {!networkError && loading && (
            <LoadingState branded message="Loading notifications…" className="py-16" />
          )}

          {!networkError && error && <p className="text-red-500 text-center py-8">{error}</p>}

          {!networkError && !loading && !error && notifications.length === 0 && (
            <EmptyState
              icon={FaBell}
              title="No notifications yet."
              description="You have no new notifications at the moment. Updates about bookings and reports will appear here."
            />
          )}

          {!networkError && !loading && !error && notifications.length > 0 && (
            <div className="space-y-5 sm:space-y-6">
              {Object.entries(grouped).map(([label, items]) => (
                <div key={label}>
                  <p className="text-xs font-semibold uppercase tracking-wide text-on-surface-variant mb-2 sm:mb-3 px-0.5">
                    {label}
                  </p>
                  <div className="space-y-2">
                    {items.map((notification) => {
                      const unread = !notification.isRead;
                      return (
                        <button
                          key={notification._id || notification.timestamp}
                          type="button"
                          onClick={() => handleMarkRead(notification)}
                          className={`w-full text-left bg-surface-lowest rounded-xl border p-3 sm:p-4 flex items-start gap-3 min-h-[56px] transition-colors hover:bg-surface-low active:bg-surface-low ${
                            unread ? "border-l-4 border-l-primary border-surface-container" : "border-surface-container opacity-80"
                          }`}
                        >
                          {getIcon(notification.title)}
                          <div className="flex-1 min-w-0">
                            <div className="flex items-start justify-between gap-2">
                              <h3 className={`text-sm leading-snug break-words ${unread ? "font-bold text-on-primary-fixed-var" : "font-medium text-on-primary-fixed-var"}`}>
                                {notification.title}
                              </h3>
                              {unread && (
                                <span className="shrink-0 w-2 h-2 rounded-full bg-primary mt-1.5" aria-label="Unread" />
                              )}
                            </div>
                            <p className="text-sm text-on-surface-variant mt-1 leading-relaxed break-words">
                              {notification.message}
                            </p>
                            <p className="text-xs text-on-surface-variant mt-2">{timeAgo(notification.timestamp)}</p>
                          </div>
                        </button>
                      );
                    })}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
        <Footer />
      </div>
    </div>
  );
};

export default NotificationsPage;
